import EChartCommon, { getAxisPointer } from '@/pages/opinion/components/EChartCommon';
import React, { useMemo } from 'react';
import { formatTimeXAxisText, getChartPosition } from '@/pages/opinion/utils/chart';
import * as echarts from 'echarts';
import { formatChartParamsDate, listTimeTitleFunc } from '@/pages/opinion/components/ChartCommon/formatter';
import { useIntl } from 'umi';
import { NO_DATE } from '@/pages/opinion/constants';
import { reviewsFormat } from '@/pages/opinion/utils/value';
import usePcConsoleTimelineChart from '@op/pages/gamestore/hooks/usePcConsoleTimelineChart';
import { ServerTimeType } from '@op/components/FilterCommon/types';

// 此组件基于舆情 MetacriticScoreTrendBox 组件改造

interface FilterParams {
  date_type: ServerTimeType;
}

interface PlatformDataItem {
  [key: string]: string;
  timeline_of_end_time: string;
  timeline_of_start_time: string;
}

interface SeriesDataKey {
  text: string;
  key: string;
  type: 'line' | 'bar';
  color: string;
  valueKey: string;
  isPercent: boolean;
}

interface TrendChartProps {
  filterParams: FilterParams;
  platformData: PlatformDataItem[];
  height?: number;
  seriesDataKeys: SeriesDataKey[];
}
export default function TrendChart({
  filterParams,
  platformData,
  height = 60,
  seriesDataKeys,
}: TrendChartProps) {
  const { formatMessage } = useIntl();
  const { chartMap, chartNoDataMap } = usePcConsoleTimelineChart({
    platformData,
    filterParams,
  });

  const options = useMemo(() => {
    const legends: { name: string; itemStyle: { color: string; }; }[] = [];
    const series: {
      yAxisIndex: number; name: string; type: string; data: any[]; isPercent: boolean;
    }[] = [];
    let xAxis: string[] = [];
    seriesDataKeys.map(({ text, key, type, color, valueKey, isPercent }, index) => {
      const data = chartMap?.[key];
      if (data) {
        
        const seriesObj: any = {
          name: text,
          type,
          data: data.map((item: { [x: string]: any; }) =>
            item?.[valueKey] ? item[valueKey] : null,
          ),
          itemStyle: {
            color,
          },
          areaStyle: {
            opacity: 0.3,
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color },
              { offset: 1, color: 'rgba(255,255,255,0)' },
            ]),
          },
          key,
          yAxisIndex: index,
          isPercent,
        };

        series.push(seriesObj);
        legends.push({
          name: text,
          itemStyle: { color },
        });

        if (xAxis.length === 0 && data) {
          xAxis = data.map((item: { time: any; }) => item.time);
        }
      }
    });
    const currentOptions = {
      tooltip: {
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any]; },
        ) =>
          getChartPosition(pos, params, dom, rect, size, 50, 15, 'bottom', 3),
        appendToBody: false,
        confine: false,
        formatter: (params: any) => {
          const title = listTimeTitleFunc(
            params?.[0]?.axisValue,
            filterParams.date_type,
            true,
          );
          return formatChartParamsDate({
            params,
            title,
            listTitleFunc: (params) =>
              formatMessage({ id: params.seriesName || NO_DATE.EMPTY }),
            valueFormat: (value, param) => {
              if (param.seriesName === 'Critics Recommend%') {
                return `${reviewsFormat(value)}%`;
              }
              return reviewsFormat(value);
            },
          });
        },
        
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filterParams?.date_type),
      ),
      grid: {
        top: 10,
        bottom: 0,
        left: 0,
        right: 0,
        containLabel: true,
      },
      legend: {
        show: false,
        top: 0,
        bottom: 0,
        height: 0,
      },
      xAxis: {
        boundaryGap: ['20%', 0],
        data: xAxis,
        axisLabel: {
          showMaxLabel: true,
          color: '#BBBDCB',
          fontSize: 8,
          formatter: (value: string) =>
            formatTimeXAxisText(value, filterParams?.date_type),
        },
        splitLine: { show: false },
      },
      yAxis: seriesDataKeys?.map(({ isPercent }) => ({
        type: 'value',
        splitLine: { show: false },
        axisLabel: {
          color: '#BBBDCB',
          fontSize: 8,
          formatter: (value: number) => isPercent ? `${value}%` : value,
        },
      })),
      series,
    };

    return currentOptions;
  }, [seriesDataKeys, chartMap, filterParams.date_type, formatMessage]);

  return (
    <EChartCommon
      options={options}
      height={height}
      empty={chartNoDataMap}
      optionsProps={{ type: 'line' }}
    ></EChartCommon>
  );
}
